import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import './AuthPage.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const { verifyResetToken, resetPassword } = useAuth();
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [isTokenVerifying, setIsTokenVerifying] = useState(true);
  const [isResetComplete, setIsResetComplete] = useState(false);
  
  const token = searchParams.get('token');

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setIsTokenVerifying(false);
        return;
      }

      try {
        await verifyResetToken(token);
        setIsTokenValid(true);
      } catch (error) {
        setIsTokenValid(false);
      } finally {
        setIsTokenVerifying(false);
      }
    };

    checkToken();
  }, [token, verifyResetToken]);

  const initialValues = {
    password: '',
    confirmPassword: ''
  };

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      )
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Please confirm your password')
  });

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      setStatus(null);
      await resetPassword(token, values.password);
      setIsResetComplete(true);
    } catch (error) {
      setStatus({ error: error.message || 'Failed to reset password. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state while verifying token
  if (isTokenVerifying) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-modal">
          <div className="auth-page-content">
            <h2>Verifying Reset Link</h2>
            <div className="loading-container">
              <span className="loading-spinner" aria-hidden="true"></span>
              <span>Verifying your reset link...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!isTokenValid) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-modal">
          <div className="auth-page-content">
            <h2>Invalid Reset Link</h2>
            
            <div className="alert alert-danger">
              <p>
                This password reset link is invalid or has expired.
              </p>
              <p>
                Password reset links are only valid for 24 hours for security reasons.
              </p>
            </div>

            <div className="form-footer">
              <p>
                <Link to="/forgot-password" className="link-text">
                  Request a new reset link
                </Link>
              </p>
              <p>
                <Link to="/login" className="link-text">
                  Back to Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password reset successful
  if (isResetComplete) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-modal">
          <div className="auth-page-content">
            <h2>Password Reset Successful</h2>
            
            <div className="alert alert-success">
              <p>
                Your password has been successfully reset!
              </p>
              <p>
                You can now log in with your new password.
              </p>
            </div>

            <div className="form-footer">
              <Link to="/login" className="btn btn-primary">
                Continue to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="auth-page-container">
      <div className="auth-page-modal">
        <div className="auth-page-content">
          <h2>Reset Your Password</h2>
          
          <p className="form-description">
            Enter your new password below. Make sure it's strong and secure.
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, status }) => (
              <Form>
                {status && status.error && (
                  <div className="alert alert-danger">{status.error}</div>
                )}
              
                <div className="form-group">
                  <label htmlFor="password">New Password</label>
                  <Field
                    type="password"
                    name="password"
                    id="password"
                    className="form-control"
                    placeholder="Enter your new password"
                    aria-describedby="password-error password-help"
                  />
                  <ErrorMessage 
                    name="password" 
                    component="div" 
                    className="error-message"
                    id="password-error"
                    role="alert"
                  />
                  <div id="password-help" className="form-help">
                    Password must be at least 8 characters with uppercase, lowercase, and number
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <Field
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    className="form-control"
                    placeholder="Confirm your new password"
                    aria-describedby="confirmPassword-error"
                  />
                  <ErrorMessage 
                    name="confirmPassword" 
                    component="div" 
                    className="error-message"
                    id="confirmPassword-error"
                    role="alert"
                  />
                </div>
              
                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="btn btn-primary"
                  aria-describedby={isSubmitting ? "loading-text" : undefined}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner" aria-hidden="true"></span>
                      <span id="loading-text">Resetting Password...</span>
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              
                <div className="form-footer">
                  <p>
                    <Link to="/login" className="link-text">
                      Back to Login
                    </Link>
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;